/**
 * UPI deep-link helpers for mobile browser payments (no payment gateway).
 */

import { PAYMENT_MERCHANT } from '../config/paymentMerchant';

const DEBUG_UPI =
  process.env.NODE_ENV === 'development' || process.env.REACT_APP_DEBUG_UPI === 'true';

/** When true, UPI QR/intent omit `am=` so GPay/PhonePe leave amount editable (test only). */
export function isUpiAmountEditable() {
  return String(process.env.REACT_APP_UPI_EDITABLE_AMOUNT || '').toLowerCase() === 'true';
}

export function logUpiDebug(event, payload = {}) {
  if (!DEBUG_UPI) return;
  // eslint-disable-next-line no-console
  console.debug('[UPI]', event, payload);
}

/** Parse "₹500", "₹5,000", "Rs 500" → number or null. */
export function parsePaymentAmount(displayAmount) {
  if (typeof displayAmount === 'number' && Number.isFinite(displayAmount)) {
    return displayAmount;
  }
  const cleaned = String(displayAmount || '').replace(/[^\d.]/g, '');
  if (!cleaned) return null;
  const value = parseFloat(cleaned);
  return Number.isFinite(value) ? value : null;
}

export function formatUpiAmount(amount) {
  return Number(amount).toFixed(2);
}

/**
 * Build standard UPI URI. Uses encodeURIComponent (not URLSearchParams) so spaces
 * become %20 — some UPI apps fail intents when payee name uses + encoding.
 * Set omitAmount / REACT_APP_UPI_EDITABLE_AMOUNT=true to leave amount editable in UPI apps.
 */
export function buildUpiDeepLink({
  vpa,
  payeeName,
  amount,
  note,
  minimal = false,
  omitAmount = false,
}) {
  const parts = [`pa=${encodeURIComponent(vpa)}`];
  if (!minimal) {
    if (payeeName) parts.push(`pn=${encodeURIComponent(payeeName)}`);
    if (note) parts.push(`tn=${encodeURIComponent(String(note).slice(0, 80))}`);
  }
  if (!omitAmount && amount != null && amount !== '') {
    parts.push(`am=${encodeURIComponent(formatUpiAmount(amount))}`);
  }
  parts.push('cu=INR');
  return `upi://pay?${parts.join('&')}`;
}

/** Full QR payload (scan from another device / camera). */
export function resolvePaymentUpiLink({ displayAmount, planName }) {
  const amount = parsePaymentAmount(displayAmount);
  const vpa = PAYMENT_MERCHANT.upiId;
  if (!vpa || amount == null) return null;

  const note = planName
    ? `${PAYMENT_MERCHANT.transactionNotePrefix} - ${planName}`
    : PAYMENT_MERCHANT.transactionNotePrefix;

  return buildUpiDeepLink({
    vpa,
    payeeName: PAYMENT_MERCHANT.payeeName,
    amount,
    note,
    minimal: false,
    omitAmount: isUpiAmountEditable(),
  });
}

/**
 * Intent for "Pay with Any UPI App" on the same phone.
 * Uses the same full payee/note fields as the QR so Axis merchant VPAs resolve consistently.
 */
export function resolvePaymentUpiIntentLink({ displayAmount, planName }) {
  const amount = parsePaymentAmount(displayAmount);
  const vpa = PAYMENT_MERCHANT.upiId;
  if (!vpa || amount == null) return null;

  const note = planName
    ? `${PAYMENT_MERCHANT.transactionNotePrefix} - ${planName}`
    : PAYMENT_MERCHANT.transactionNotePrefix;

  return buildUpiDeepLink({
    vpa,
    payeeName: PAYMENT_MERCHANT.payeeName,
    amount,
    note,
    minimal: false,
    omitAmount: isUpiAmountEditable(),
  });
}

/** Browser metadata for troubleshooting logs only. */
export function getBrowserDebugInfo() {
  if (typeof navigator === 'undefined') {
    return { platform: 'unknown', browser: 'unknown', device_type: 'unknown', userAgent: '' };
  }
  const ua = navigator.userAgent || '';
  let platform = 'other';
  if (/Android/i.test(ua)) platform = 'android';
  else if (/iPhone|iPad|iPod/i.test(ua)) platform = 'ios';

  let browser = 'other';
  if (/Edg\//i.test(ua)) browser = 'edge';
  else if (/Chrome/i.test(ua)) browser = 'chrome';
  else if (/Safari/i.test(ua)) browser = 'safari';
  else if (/Firefox/i.test(ua)) browser = 'firefox';

  const device_type =
    platform === 'android' || platform === 'ios' ? 'mobile' : 'desktop';

  return { platform, browser, device_type, userAgent: ua };
}
/** True on phones/tablets (Android/iOS). Not true for laptop + Chrome DevTools resize. */
export function isMobileUserAgent() {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod|Mobile|IEMobile|Opera Mini/i.test(navigator.userAgent || '');
}

/** UPI deep links (upi://) only work on real mobile devices with a UPI app installed. */
export function supportsUpiDeepLink() {
  return isMobileUserAgent();
}

/** @deprecated Prefer supportsUpiDeepLink() for pay actions; kept for layout hints. */
export function isMobileBrowser() {
  return supportsUpiDeepLink();
}

/**
 * Open UPI app picker on a phone. On laptop/desktop, calls onUnsupported instead of
 * navigating (avoids "scheme does not have a registered handler").
 */
export function openUpiDeepLink(
  url,
  { onMaybeUnavailable, onUnsupported, delayMs = 2500 } = {}
) {
  if (!supportsUpiDeepLink()) {
    logUpiDebug('deep_link_unsupported', { url, reason: 'desktop_or_no_mobile_ua' });
    onUnsupported?.();
    return;
  }

  logUpiDebug('deep_link_open', { url, ...getBrowserDebugInfo() });
  const openedAt = Date.now();
  window.location.href = url;

  window.setTimeout(() => {
    if (document.visibilityState === 'visible' && Date.now() - openedAt < delayMs + 500) {
      onMaybeUnavailable?.();
    }
  }, delayMs);
}

export async function sharePaymentDetails({ payeeName, amountLabel, upiId, planName }) {
  if (!navigator.share) return false;
  const lines = [
    `Pay ${amountLabel} to ${payeeName}.`,
    upiId ? `UPI ID: ${upiId}` : null,
    planName ? `Plan: ${planName}` : null,
    'Complete payment in your UPI app, then upload proof on the website.',
  ].filter(Boolean);

  try {
    await navigator.share({
      title: 'I-Connect payment',
      text: lines.join('\n'),
    });
    logUpiDebug('share_success', { amountLabel, upiId: upiId ? '[set]' : '[missing]' });
    return true;
  } catch (err) {
    if (err?.name !== 'AbortError') {
      logUpiDebug('share_failed', { message: err?.message });
    }
    return false;
  }
}

export function canSharePayment() {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}
